import {
  AiGenerateBulkProductsRequest,
  AiGenerateBulkProductsResponse,
  AiGeneratedProduct,
  AiBulkProductItem,
} from "../../../interfaces/AiObject";
import { turboField } from "../../helpers/TurboStore";
import { Category } from "../../../interfaces/AdminObject";
import { BusinessProfile } from "../../../interfaces/BusinessObject";
import {
  CategoryAttributeConfig,
  ListingTypeConfig,
  SharedAttributeConfig,
  SharedAttributeOption,
} from "../../../interfaces/ProductObject";
import {
  AnalyzeSectionContext,
  BulkImageAnalyzeContext,
} from "../../../interfaces/ImageAnalyzeObject";
import { BusinessSectionNode } from "../../../interfaces/BusinessSectionObject";

/**
 * Section hints for the vision/text-generation flows — root name plus its
 * immediate children's names. This shape (and the equivalent inline prompt
 * text below) has no way to express more than 2 levels — the same cap the
 * legacy SECTION/SUBSECTION keys always had — so anything deeper in the tree
 * is invisible to the model regardless of which flow sends it. Shared by
 * NineSetupBulkActions and BusinessBulkUploadActions so both AI flows build
 * this the same way from the same tree shape.
 */
function buildSectionHints(tree: BusinessSectionNode[]): AnalyzeSectionContext[] {
  return tree.map((root) =>
    root.children.length > 0
      ? { name: root.name, subsections: root.children.map((c) => c.name) }
      : { name: root.name }
  );
}

/** Renders `buildSectionHints`' output as "Root (Subsections: a, b)" strings,
 *  for a flow that embeds the section list in a free-text prompt instead of
 *  (or in addition to) the structured `sections` field. */
function formatSectionHintsAsText(hints: AnalyzeSectionContext[]): string[] {
  return hints.map((hint) =>
    hint.subsections?.length
      ? `${hint.name} (Subsections: ${hint.subsections.join(", ")})`
      : hint.name
  );
}

function buildAutofillPayload(imageFormData: FormData, age: number): FormData {
  const imageFiles = imageFormData.getAll("files");

  if (!imageFiles || imageFiles.length === 0) {
    throw new Error("No image files provided");
  }

  const payload = new FormData();

  imageFiles.forEach((file) => {
    if (file instanceof File) {
      payload.append("images", file);
    }
  });

  payload.append("age", age.toString());

  return payload;
}

/**
 * Builds payload for market price suggestion API
 * @param imageFormData - FormData containing image files under 'files' key
 * @param productDetails - Product metadata object
 * @returns FormData with image and product details
 */
function buildMarketPricePayload(
  imageFormData: FormData,
  productDetails: any,
): FormData {
  const imageFiles = imageFormData.getAll("files");

  if (!imageFiles || imageFiles.length === 0) {
    throw new Error("No image files provided");
  }

  const payload = new FormData();

  imageFiles.forEach((file) => {
    if (file instanceof File) {
      payload.append("images", file);
    }
  });

  // Clean productDetails to remove redundant/unnecessary fields
  const cleanedProductDetails = {
    ...productDetails,
    // Remove draft media from essentials (bytes travel separately as files)
    essentials: {
      ...productDetails.essentials,
      media: undefined,
    },
    // Keep only originalPrice from strategy
    strategy: productDetails.strategy?.originalPrice
      ? { originalPrice: productDetails.strategy.originalPrice }
      : undefined,
    // Remove meta entirely
    meta: undefined,
  };

  // Append product details as JSON string
  payload.append("productDetails", JSON.stringify(cleanedProductDetails));

  return payload;
}

/**
 * Builds the multipart body for /api/v1/products/bulkImageAnalyze.
 *
 * Two parts: `images` repeated once per file, and a single `context` part
 * carrying the JSON.
 *
 * The Blob wrapper on `context` is NOT decoration and must not be simplified to
 * `append("context", JSON.stringify(ctx))`. Most HTTP clients set a content
 * type on file parts and leave string parts bare, and this endpoint rejects a
 * bare context part with a 415 raised by Spring before any backend code runs —
 * so the failure arrives as an unrecognisable framework error page rather than
 * anything this app's error helper can read. Wrapping the JSON in a typed Blob
 * is what puts `Content-Type: application/json` on that part.
 *
 * Note that the browser sets the multipart boundary itself, which is why
 * httpRequest.postRequest deliberately does not set a Content-Type header when
 * the payload is FormData — setting one by hand omits the boundary and breaks
 * the whole request.
 */
function buildBulkImageAnalyzePayload(
  images: File[],
  context: BulkImageAnalyzeContext
): FormData {
  if (!images || images.length === 0) {
    throw new Error("No images provided");
  }

  const payload = new FormData();

  // Repeat the part name once per file — the backend reads `images` as a list.
  images.forEach((file) => payload.append("images", file));

  // Do NOT simplify to append("context", JSON.stringify(context)). The Blob is
  // the only way to put a Content-Type on this part, and without it the request
  // is rejected with a 415 by Spring before any backend code runs. See the
  // javadoc above.
  payload.append(
    "context",
    new Blob([JSON.stringify({ ...context, ...turboField(context.business?.businessId) })], {
      type: "application/json",
    })
  );

  return payload;
}

/**
 * Builds the `context` part from what the Nine Setup bulk modal already holds:
 * the fetched business and the setup row's own coordinates.
 *
 * Everything is omitted rather than sent as null when it isn't known. That's
 * the difference between a field the backend reports in
 * `missingRequiredFields` — which the review screen turns into a required
 * input the curator fills — and a field it believes was answered with nothing.
 *
 * `sellerId` is deliberately never set here — a curator building a business page
 * is not the seller. It is also not required and never read from the create
 * payload: the server derives it from the auth context for personal listings
 * only, and a business product is stored without one. So its absence costs
 * nothing and should not appear in `missingRequiredFields` either.
 */
function buildBulkImageAnalyzeContext(params: {
  business: BusinessProfile | null;
  latitude?: number | null;
  longitude?: number | null;
  sellerNotes?: string;
  age?: string | number | null;
  condition?: string;
  sections?: AnalyzeSectionContext[];
  contactMode?: string;
}): BulkImageAnalyzeContext {
  const context: BulkImageAnalyzeContext = {};

  if (params.business) {
    context.business = {
      businessId: params.business.businessId,
      name: params.business.name,
      ...(params.business.listingType
        ? { listingType: params.business.listingType }
        : {}),
    };
    if (params.business.listingType) {
      context.listingType = params.business.listingType;
    }
  }

  if (params.latitude != null) context.latitude = params.latitude;
  if (params.longitude != null) context.longitude = params.longitude;

  if (params.contactMode) context.contactMode = params.contactMode;

  const notes = params.sellerNotes?.trim();
  if (notes) context.sellerNotes = notes;

  // Years, and only when it's actually a number — the backend copies it to
  // product.age verbatim and never guesses it, so a stray "" or NaN would be a
  // worse answer than no answer.
  if (params.age !== undefined && params.age !== null && params.age !== "") {
    const age = typeof params.age === "number" ? params.age : Number(params.age);
    if (Number.isFinite(age)) context.age = age;
  }

  const condition = params.condition?.trim();
  if (condition) context.condition = condition;

  // "others" is always available to the model as a fallback, so sending it is a
  // no-op that only makes the offered list look longer than it is.
  const sections = (params.sections ?? []).filter(
    (section) => section.name?.trim() && section.name.trim().toLowerCase() !== "others"
  );
  if (sections.length > 0) context.sections = sections;

  return context;
}

/**
 * Turns analysed drafts into the `{ product: {...} }[]` shape `payloadText`
 * already holds, so everything downstream — deriveBulkPreviewRows, the
 * required-field gate, the apply-to-all buttons, bulkCreateProduct — treats
 * them exactly like a pasted payload. Nothing downstream needed to change.
 *
 * The one rule: each draft is passed through UNTOUCHED. Not merged, not
 * filtered, not remapped, not stripped of its nulls. It arrived as a
 * CreateProductRq and it leaves as the same object, because every field copied
 * by hand here is a field that silently vanishes the day the backend adds one.
 * That is also why this function is four lines and takes no options — anything
 * it did to a draft would be a bug waiting for a schema change.
 *
 * Contrast mapGeneratedProductsToBulkItems below, which reshapes heavily. That
 * endpoint returns loose AI answers that were never a request object; this one
 * returns the request object itself.
 */
function mapAnalyzedDraftsToBulkItems(
  drafts: Record<string, unknown>[]
): unknown[] {
  return drafts.map((product) => ({ product }));
}

/**
 * Builds the request for /api/v1/products/generateBulkProducts (see
 * AiObject.ts for the full contract and the reasoning behind it) from
 * whatever NineSetupBulkActions already has on hand: the fetched business,
 * its listing type config, the curator's single chosen category (and that
 * category's attribute schema, already narrowed to this listing type), the
 * listing type's shared attributes, and the four generator fields.
 *
 * Deliberately thin — no business logic here beyond reshaping. Category and
 * listing type are sent for CONTEXT (so the AI writes relevant copy); they
 * are not asked for back, because mapGeneratedProductsToBulkItems below
 * stamps them onto every item deterministically, the same way "Apply to all"
 * stamps businessId/location/section today.
 */
function buildGenerateBulkProductsPayload(params: {
  business: BusinessProfile;
  listingTypeConfig: ListingTypeConfig | null;
  listingTypeLabel: string;
  category: Category;
  categoryAttributes: CategoryAttributeConfig[];
  sharedAttributes: SharedAttributeConfig[];
  count: number;
  productContext: string;
  prompt: string;
}): AiGenerateBulkProductsRequest {
  const optionsToStrings = (
    options: string[] | SharedAttributeOption[]
  ): string[] =>
    options.map((opt) => (typeof opt === "string" ? opt : opt.value));

  const pricing = params.listingTypeConfig?.pricing;

  return {
    business: {
      businessId: params.business.businessId,
      name: params.business.name,
      description: params.business.description,
      listingType: params.business.listingType,
      listingTypeLabel: params.listingTypeLabel,
    },
    category: {
      name: params.category.name,
      attributes: params.categoryAttributes.map((attr) => ({
        name: attr.name,
        label: attr.label,
        type: attr.type,
        required: attr.required,
        options: attr.options,
        min: attr.min,
        max: attr.max,
        unit: attr.unit,
      })),
    },
    sharedAttributes: params.sharedAttributes.map((attr) => ({
      name: attr.name,
      label: attr.label,
      type: attr.type,
      required: attr.required,
      options: optionsToStrings(attr.options),
    })),
    pricing: pricing
      ? {
          model: pricing.model,
          label: pricing.label ?? "",
          negotiableToggle: pricing.negotiable_toggle ?? false,
          showFreeOption: pricing.show_free_option ?? false,
        }
      : null,
    generation: {
      count: params.count,
      productContext: params.productContext.trim() || undefined,
      prompt: params.prompt.trim() || undefined,
    },
  };
}

/**
 * Strategy-field defaults stamped onto every generated product (Sai,
 * 2026-08-17) — applied right after the AI response comes back, before
 * anything is written into `payloadText`. The AI's own value wins when the
 * response actually supplies one for that field; these only fill the gap.
 */
const DEFAULT_IS_NEGOTIABLE = true;
const DEFAULT_CONTACT_MODE: NonNullable<AiGeneratedProduct["contactMode"]> =
  "AUTO";
const DEFAULT_DISPLAY_PRICE = true;

/**
 * Turns the AI's response into the exact shape NineSetupBulkActions'
 * `payloadText` already expects — an array of `{ product: {...} }`, wrapper
 * and all, so it can be JSON.stringify'd straight into the textarea and run
 * through the SAME deriveBulkPreviewRows/required-field validation a pasted
 * payload gets. Nothing downstream needs to know these items came from the
 * AI rather than a paste.
 *
 * Reads `generated.details`/`condition`/`age` — the real field names
 * confirmed 2026-08-16, not the `attributes` map an earlier draft of this
 * function expected (that mismatch was why every generated product's
 * details came back empty the first time this ran against a live response).
 *
 * Four things happen per item that the AI is never asked to do itself:
 *  - `category` / `listingType` are stamped from what the curator already
 *    picked. The response DOES echo a `category` back, but it's ignored —
 *    the curator's own selection is the source of truth, not something to
 *    trust from the model.
 *  - `section` is stamped the same way, from the one section field for the
 *    whole batch (see AiObject.ts on why it was never sent to the AI at all).
 *  - a non-null `condition` is mirrored into a top-level `condition` field in
 *    ADDITION to `details.Condition` — matching buildCreateProductPayload's
 *    own behaviour for a single product (condition lives in both places
 *    there too).
 *  - `isNegotiable` / `contactMode` / `displayPrice` are always set, at the
 *    same level as `title` — the AI's value if the response carries one for
 *    that product, DEFAULT_IS_NEGOTIABLE / DEFAULT_CONTACT_MODE /
 *    DEFAULT_DISPLAY_PRICE above otherwise. Unlike `suggestedPrice`, which
 *    stays genuinely optional (omitted when absent), these three are never
 *    left out of the payload.
 *
 * Null-valued keys in `details` (the backend sends explicit nulls for
 * attributes it had no answer for, e.g. `"fuel_type": null`) are dropped
 * rather than written into the payload — a literal `"fuel_type": null` would
 * otherwise sit in every product's details forever, since nothing downstream
 * ever clears it back out.
 */
function mapGeneratedProductsToBulkItems(
  response: AiGenerateBulkProductsResponse,
  context: {
    category: string;
    listingTypeLabel: string;
  }
): unknown[] {
  return response.products.map((generated) => {
    const details: Record<string, string | number> = {};

    Object.entries(generated.details || {}).forEach(([name, value]) => {
      if (value === undefined || value === null || value === "") return;
      details[name] = value;
    });

    if (generated.condition) {
      details.Condition = generated.condition;
    }
    if (
      generated.age !== undefined &&
      generated.age !== null &&
      generated.age !== ""
    ) {
      details.age = generated.age;
    }
    // Section is no longer stamped at generation time — the curator applies
    // one (via the tree picker) in the preview step afterward, the same way
    // as for a pasted, cloned, or analyzed payload. One mechanism for every
    // payload source, rather than a free-text shortcut just for this one.

    const product: AiBulkProductItem = {
      title: generated.title,
      description: generated.description,
      category: context.category,
      listingType: context.listingTypeLabel,
      sellingPrice: generated.sellingPrice,
      details,
      // Always set — AI's value if it gave one for this product, the fixed
      // default otherwise. Never omitted.
      isNegotiable: generated.isNegotiable ?? DEFAULT_IS_NEGOTIABLE,
      contactMode: generated.contactMode ?? DEFAULT_CONTACT_MODE,
      displayPrice: generated.displayPrice ?? DEFAULT_DISPLAY_PRICE,
      ...(generated.suggestedPrice != null
        ? { suggestedPrice: generated.suggestedPrice }
        : {}),
      ...(generated.condition ? { condition: generated.condition } : {}),
    };

    return { product };
  });
}

const AiConvertor = {
  buildAutofillPayload,
  buildMarketPricePayload,
  buildGenerateBulkProductsPayload,
  mapGeneratedProductsToBulkItems,
  buildBulkImageAnalyzePayload,
  buildBulkImageAnalyzeContext,
  mapAnalyzedDraftsToBulkItems,
  buildSectionHints,
  formatSectionHintsAsText,
};

export default AiConvertor;
